/*
 * Dashboard data derivations. Pure functions over already-fetched shapes —
 * no network, no React, no date library. The `now` argument is always
 * required so every unit test pins a deterministic clock.
 *
 * Every gate-check the dashboard performs is also expressed here: a widget
 * or alert row appears only if the selector returns something. Permission /
 * role gating happens in the page component; these selectors assume the
 * caller has already decided whether to invoke them.
 */

import type { SessionRecord } from '@/api/sessions';
import type { ClientAuthAlertRow, ClientCountsResponse } from '@/api/clients';
import type { ClaimStats } from '@/api/claims';
import type { MyProfile } from '@/api/users';
import type {
    BillingSettings,
    NotificationSettings,
} from '@/api/settings';
import type { PendingSignaturesSummary } from '@/api/portal';

// ── Shared types ──────────────────────────────────────────────────

export type AlertLevel = 'danger' | 'warn' | 'info';
export type AlertCategory = 'clinical' | 'billing' | 'compliance' | 'portal' | 'info';

export interface DashboardAlert {
    id: string;
    level: AlertLevel;
    category: AlertCategory;
    title: string;
    description: string;
    /** Relative route to link to when the row is clicked. Optional. */
    href?: string;
}

// ── Today-session KPI ─────────────────────────────────────────────

export interface TodaySessionsKpi {
    total: number;
    complete: number;
    cancelled: number;
    remaining: number;
}

export function countTodaySessions(
    sessions: readonly SessionRecord[] | undefined,
    todayIso: string,
): TodaySessionsKpi {
    const today = (sessions ?? []).filter((s) => s.date === todayIso);
    let complete = 0, cancelled = 0, remaining = 0;
    for (const s of today) {
        if (s.status === 'Complete' || s.status === 'Finalized') complete++;
        else if (s.status === 'Canceled') cancelled++;
        else remaining++;
    }
    return { total: today.length, complete, cancelled, remaining };
}

// ── Notes-needing-attention KPI ───────────────────────────────────

export interface NotesNeedingAttentionKpi {
    total: number;
    missing: number;
    drafting: number;
    ready: number;
}

/**
 * Count sessions whose note needs STAFF action. Only sessions whose
 * session status is Complete count — Scheduled hasn't happened yet,
 * Canceled has no note expectation, and Finalized is done. `Submitted`
 * notes are blocked on the parent's signature, not on staff, so they
 * don't count here — they surface as the "Pending parent signatures"
 * alert instead.
 */
export function countNotesNeedingAttention(
    sessions: readonly SessionRecord[] | undefined,
): NotesNeedingAttentionKpi {
    let missing = 0, drafting = 0, ready = 0;
    for (const s of sessions ?? []) {
        if (s.status !== 'Complete') continue;
        const n = s.noteStatus;
        if (n === '') missing++;
        else if (n === 'Drafting') drafting++;
        else if (n === 'Complete') ready++;
        // 'Submitted' → waiting on parent; 'Finalized' → done.
    }
    return { total: missing + drafting + ready, missing, drafting, ready };
}

// ── Client roster KPI ─────────────────────────────────────────────

export interface ClientCountKpi {
    active: number;
    total: number;
}

export function countClients(counts: ClientCountsResponse | undefined): ClientCountKpi {
    const active = counts?.byStatus['Active'] ?? 0;
    const total = counts?.total ?? 0;
    return { active, total };
}

// ── Claims KPI ─────────────────────────────────────────────────────

export interface ClaimsKpi {
    attention: number;
    denied: number;
    ready: number;
}

export function countClaimsNeedingAttention(stats: ClaimStats | undefined): ClaimsKpi {
    const denied = stats?.denied ?? 0;
    const ready = stats?.ready ?? 0;
    return { attention: denied + ready, denied, ready };
}

// ── Day-of-week session chart ─────────────────────────────────────

/**
 * Returns a 7-element array (Mon…Sun) of counts of non-cancelled sessions
 * with a `date` inside the given inclusive window. Canceled sessions are
 * excluded since the chart exists to show delivered/planned activity.
 */
export function dayOfWeekCounts(
    sessions: readonly SessionRecord[] | undefined,
    windowStartIso: string,
    windowEndIso: string,
): number[] {
    const counts = [0, 0, 0, 0, 0, 0, 0];
    for (const s of sessions ?? []) {
        if (!s.date) continue;
        if (s.date < windowStartIso || s.date > windowEndIso) continue;
        if (s.status === 'Canceled') continue;
        const d = new Date(`${s.date}T00:00:00`);
        const dow = (d.getDay() + 6) % 7; // Mon=0 … Sun=6
        counts[dow]++;
    }
    return counts;
}

// ── Alert builder ─────────────────────────────────────────────────

export interface BuildAlertsInput {
    sessions: readonly SessionRecord[] | undefined;
    /** Slim auth-alert rows from /api/clients/counts. */
    clients: readonly ClientAuthAlertRow[] | undefined;
    claimStats: ClaimStats | undefined;
    /**
     * Own profile — retained for future per-user dashboard surfaces.
     * The cert-expiry alert that previously consumed it was retired
     * 2026-05-19; compliance lives in admin-server now.
     */
    myProfile: MyProfile | undefined;
    pendingSigs: PendingSignaturesSummary | undefined;
    settings: {
        billing?: BillingSettings;
        notification?: NotificationSettings;
    };
    perms: ReadonlySet<string>;
    todayIso: string;
}

function daysBetween(toIso: string, todayIso: string): number {
    const to = Date.parse(`${toIso}T00:00:00`);
    const from = Date.parse(`${todayIso}T00:00:00`);
    if (Number.isNaN(to) || Number.isNaN(from)) return NaN;
    return Math.round((to - from) / 86_400_000);
}

export function buildAlerts(input: BuildAlertsInput): DashboardAlert[] {
    const { perms, settings, todayIso } = input;
    const notif = settings.notification ?? {};
    const billing = settings.billing ?? {};
    const alerts: DashboardAlert[] = [];

    const has = (p: string) => perms.has(p);
    const hasViewAll = has('view_all_clients') || has('view_billing') || has('edit_billing')
        || has('edit_scheduling') || has('delete_sessions')
        || has('export_reports')
        || has('manage_settings') || has('view_audit_log');
    const hasViewAssigned = hasViewAll || has('view_assigned_clients')
        || has('view_scheduling') || has('view_notes') || has('write_notes')
        || has('finalize_notes') || has('data_collection')
        || has('view_treatment_plans') || has('edit_treatment_plans')
        || has('view_supervision') || has('log_supervision')
        || has('view_portal');

    // Clinical: note-status rollup (respects the staff-wide "missing note" toggle)
    if (has('view_notes') && has('view_scheduling') && notif.staffMissingNote !== false) {
        const k = countNotesNeedingAttention(input.sessions);
        if (k.missing > 0) {
            alerts.push({
                id: 'notes-missing',
                level: 'warn',
                category: 'clinical',
                title: 'Notes not started',
                description: `${k.missing} complete session${k.missing === 1 ? ' has' : 's have'} no documentation`,
                href: '/scheduling',
            });
        }
        if (k.drafting > 0) {
            alerts.push({
                id: 'notes-drafting',
                level: 'info',
                category: 'clinical',
                title: 'Notes in progress',
                description: `${k.drafting} session note${k.drafting === 1 ? ' is' : 's are'} being drafted`,
                href: '/scheduling',
            });
        }
        if (k.ready > 0) {
            alerts.push({
                id: 'notes-ready',
                level: 'warn',
                category: 'clinical',
                title: 'Notes awaiting finalization',
                description: `${k.ready} session note${k.ready === 1 ? ' has' : 's have'} a complete note that still needs to be finalized`,
                href: '/scheduling',
            });
        }
    }

    // Billing: denied claims
    if (has('view_billing') && notif.staffClaimDenied !== false) {
        const denied = input.claimStats?.denied ?? 0;
        if (denied > 0) {
            alerts.push({
                id: 'claims-denied',
                level: 'danger',
                category: 'billing',
                title: 'Denied claims',
                description: `${denied} claim${denied === 1 ? ' requires' : 's require'} attention`,
                href: '/billing',
            });
        }
    }

    // Billing → authorization alerts: broadened to anyone with client-view access
    if (hasViewAssigned && notif.staffAuthExpiring !== false) {
        const expiryDays = billing.authExpiryDays ?? 30;
        const urgentDays = billing.authUrgentDays ?? 7;
        const lowPct     = billing.authLowPct ?? 10;

        for (const c of input.clients ?? []) {
            if (c.status !== 'Active' || !c.auth) continue;
            // Expired
            if (c.auth.status === 'Expired') {
                alerts.push({
                    id: `auth-expired-${c.id}`,
                    level: 'danger',
                    category: 'billing',
                    title: 'Authorization expired',
                    description: `${c.first} ${c.last} · expired authorization on file`,
                    href: `/clients/${c.id}`,
                });
                continue;
            }
            // Expiring
            if (c.auth.end) {
                const days = daysBetween(c.auth.end, todayIso);
                if (!Number.isNaN(days) && days >= 0 && days <= expiryDays) {
                    const urgent = days <= urgentDays;
                    alerts.push({
                        id: `auth-expiring-${c.id}`,
                        level: urgent ? 'danger' : 'warn',
                        category: 'billing',
                        title: 'Authorization expiring',
                        description: `${c.first} ${c.last} · expires in ${days} day${days === 1 ? '' : 's'}`,
                        href: `/clients/${c.id}`,
                    });
                }
            }
            // Low remaining hours
            if (c.auth.hours > 0) {
                const remaining = c.auth.hours - (c.auth.used || 0);
                const pct = (remaining / c.auth.hours) * 100;
                if (remaining > 0 && pct <= lowPct) {
                    alerts.push({
                        id: `auth-low-${c.id}`,
                        level: 'warn',
                        category: 'billing',
                        title: 'Low authorization hours',
                        description: `${c.first} ${c.last} · ${remaining.toFixed(1)}h remaining (${Math.round(pct)}%)`,
                        href: `/clients/${c.id}`,
                    });
                }
            }
        }
    }

    // Compliance: staff credential expiration tracking moved to admin-
    // server's compliance domain 2026-05-19 (ADR 0012). Practice admins
    // get the overdue + due-soon banner on the Home landing page; staff
    // see their own items at /me/compliance. The dashboard alert block
    // that previously lived here has been removed.

    // Portal: pending parent signatures
    if (has('view_notes') && input.pendingSigs) {
        const { notes, plans } = input.pendingSigs;
        const total = notes + plans;
        if (total > 0) {
            const parts: string[] = [];
            if (notes > 0) parts.push(`${notes} note${notes === 1 ? '' : 's'}`);
            if (plans > 0) parts.push(`${plans} plan${plans === 1 ? '' : 's'}`);
            alerts.push({
                id: 'portal-pending-sigs',
                level: 'info',
                category: 'portal',
                title: 'Pending parent signatures',
                description: `${parts.join(' and ')} awaiting parent signature`,
                href: '/portal',
            });
        }
    }

    // Info: new clients in the last 7 days (clinical or billing perm)
    if (hasViewAssigned) {
        const sevenDaysAgo = new Date(`${todayIso}T00:00:00`);
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        const threshold = sevenDaysAgo.toISOString().slice(0, 10);
        const newClients = (input.clients ?? []).filter((c) => c.intake && c.intake >= threshold);
        if (newClients.length > 0) {
            const names = newClients.slice(0, 3).map((c) => `${c.first} ${c.last}`).join(', ');
            const extra = newClients.length > 3 ? ` and ${newClients.length - 3} more` : '';
            alerts.push({
                id: 'new-clients',
                level: 'info',
                category: 'info',
                title: `New client${newClients.length === 1 ? '' : 's'}`,
                description: `${names}${extra} added in the last 7 days`,
                href: '/clients',
            });
        }
    }

    // Severity order (danger → warn → info); stable within each bucket.
    const order: Record<AlertLevel, number> = { danger: 0, warn: 1, info: 2 };
    return alerts
        .map((a, i) => ({ a, i }))
        .sort((x, y) => order[x.a.level] - order[y.a.level] || x.i - y.i)
        .map(({ a }) => a);
}
